import { css } from '@emotion/react';
import styled from '@emotion/styled';
import Cookies from 'js-cookie';
import { get, intersection, isEmpty } from 'lodash';
import { PropTypes } from 'prop-types';
import { createRef, Component, Fragment } from 'react';
import { Provider } from 'react-redux';
import Slider from 'react-slick';

import BreadcrumbsContainer from './Shingles/BreadcrumbsContainer';
import { connectFavorites } from './Shingles/Favorites/FavoritesConnectors.jsx';
import { connectRecentlyViewed } from './Shingles/Favorites/RecentlyViewedConnectors.jsx';
import useCrossTabShinglesFavoritesResync from './Shingles/Favorites/useCrossTabShinglesFavoritesResync';
import ColorSamplesContentAndImage from './Shingles/PrintedColorSamples/ContentAndImage.jsx';
import { localeCodeHasOrderSamples, shingleUidHasOrderSamples } from './Shingles/PrintedColorSamples/availability.js';
import orderSamplesUrl from './Shingles/PrintedColorSamples/orderSamplesUrl.js';
import ShingleColorDescription from './Shingles/ShingleColorDescription.jsx';
import ShingleColorsListController from './Shingles/ShingleColorsListController';
import { altText } from './Shingles/imageAlts';
import ContractorCard from './ShinglesPDP/ContractorCard';
import { Features } from './ShinglesPDP/features';
import { HelpWithColor, PYSNotes, WomensChoiceAward } from './ShinglesPDP/statics';
import withDefaultPrevented from '../../withDefaultPrevented';
import Breadcrumbs from '../Breadcrumbs';
import ContentAndImage from '../ComponentLibrary/oc-content-and-image';
import { H2 } from '../ComponentLibrary/text-elements/oc-h';
import Conditional from '../Conditional';
import { withFeatureFlagsProvider } from '../FeatureFlag';
import FeatureFlagged from '../FeatureFlag/FeatureFlagged';
import LocaleSpecific from '../LocaleSpecific';
import Modal from '../Modal';
import Reusable from '../OC/PageBuilder/Reusable';
import AlertMessage from '../OC/oc-alert-message';
import AttentionBarText from '../OC/oc-attention-bar-text';
import Cta from '../OC/oc-cta';
import Link from '../OC/oc-link.jsx';
import Picture from '../OC/oc-picture';
import RichText from '../OC/oc-rich-text.jsx';
import Section from '../OC/oc-section.jsx';
import Text from '../OC/oc-text.jsx';
import Video from '../OC/oc-video.jsx';
import { store } from '../Store.jsx';
import { If, RequestAQuoteModal, PrefixWithLocale } from '../Utilities';
import { dataTrackAttribute } from '../helpers/analytics';
import { connectLocation } from '../location/LocationConnectors';
import LocationSwitcher from '../location/Switcher';

const notFound = meta => intersection(get(meta, 'errors', []), ['not_found', 'no_zone_for_zip']).length > 0;

const alertSpacing = css`
  margin-bottom: 16px;
`;

const hasOrderSamples = (localeCode, shingleUid) =>
  localeCodeHasOrderSamples(localeCode) && shingleUidHasOrderSamples(shingleUid);

const getMediaTitle = ({ t, img }) => {
  if (!img) return '';

  const translation = t(`shingles.gallery_items.${img.name}`);
  return !translation || translation.startsWith('MISSING') ? img.name : t(`shingles.gallery_items.${img.name}`);
};

const Header = connectLocation(
  connectFavorites(
    ({ shingle, t, zip, favorites, favoritesStatus, meta, selectedColor, openLocationModal, locale }) => {
      const favoritesCount = favorites.length;
      const lastFavorite = favorites[favoritesCount - 1];
      const message = ['supreme', 'woodmoor', 'woodcrest', 'berkshire', 'trudefinition-duration-flex'].includes(
        shingle.uid
      )
        ? t('availability.title')
        : t('availability.limited_availability_title');

      const createAlertMessage = (title, textSize) => (
        <div css={alertSpacing}>
          <AlertMessage
            title={title}
            message={t('availability.message_w_link')}
            dismissable={false}
            type="warning"
            alertIcon
            textSize={textSize}
          />
        </div>
      );
      return (
        <>
          <section
            className="pdp-hero"
            style={{
              backgroundImage: `url(${shingle.hero_1984x780_url})`,
            }}
          >
            <div className="mobile-height" />
            <div className="hero-content">
              <If condition={shingle.trudefinition}>
                <span className="prehead tier3 trudefinition">
                  <Text content={t('shingles.features.trudefinition.trudefinition')} />
                </span>
              </If>
              <h1 className="tier3">
                <Text content={shingle.proper_name} />
              </h1>
              <h5 className="posthead shingles">
                <Text content={t('shingles.shingles')} />
              </h5>
            </div>
          </section>

          <Conditional if={shingle.discontinued}>
            <div className="oc-stickynav discontinued">
              <div className="stickynav-container">
                <div className="pdp-discontinued">
                  <i className="fa fa-exclamation-triangle" aria-hidden="true" />
                  <Text content={t('shingles.statics.discontinued')} />
                  <a href={PrefixWithLocale('/roofing/shingles', locale.code)}>
                    <Text content={t('shingles.statics.browse')} />
                  </a>
                </div>
              </div>
            </div>
          </Conditional>
          <Conditional if={!shingle.discontinued}>
            <div className="bg-full-bleed oc-stickynav">
              <div className="stickynav-container">
                <div className="cta-group">
                  <a
                    href={PrefixWithLocale('/roofing/shingles/favorites', locale.code)}
                    className="oc-cta-button transparent oc-icon favorites"
                    data-track="learn-more"
                    data-track-element-location="main section CTA"
                    data-track-destination="Favorites"
                  >
                    <i className={`${favoritesCount > 0 ? 'favorite-filled' : 'favorite'} icon`} aria-hidden="true" />
                    <span>
                      <span className="sr-only">
                        {`${favoritesCount <= 0 ? t('shingles.favorites_no_saved_items') : ''}`}
                      </span>
                      {t('shingles.favorites')}
                    </span>
                    &nbsp;({favoritesCount})
                    {favoritesStatus ? (
                      <span className="sr-only" role="status">
                        {`${lastFavorite} ${t(favoritesStatus)}. ${favoritesCount} ${t('shingles.favorites')}.`}
                      </span>
                    ) : null}
                  </a>

                  <FeatureFlagged except={['contractor_referral']}>
                    <LocaleSpecific only={['en-US', 'es-US']}>
                      <a
                        href={PrefixWithLocale('/roofing/contractors', locale.code)}
                        className="oc-cta-button"
                        data-track="find-a-professional"
                        data-track-professional="contractor"
                      >
                        {t('shingles.find_contractor')}
                      </a>
                    </LocaleSpecific>
                    <LocaleSpecific only={['en-CA', 'fr-CA']}>
                      <a
                        href={`http://roofingca.owenscorning.com/homeowner/locator/?zip=${
                          zip ? encodeURIComponent(zip) : ''
                        }`}
                        className="oc-cta-button"
                        data-track="find-a-professional"
                        data-track-professional="contractor"
                      >
                        {t('shingles.find_contractor')}
                      </a>
                    </LocaleSpecific>
                  </FeatureFlagged>
                </div>

                <input type="checkbox" id="mobile-stickynav" />
                <label
                  htmlFor="mobile-stickynav"
                  className="mobile-stickynav-header"
                  data-track="reveal"
                  data-track-accordion="mobile-stickynav"
                >
                  {t('owenscorning.components.sticky_nav.page_menu')}
                </label>

                <ul id="stickynav-slider" className="stickynav-links">
                  <li>
                    <a className="stickynav-anchor" href="#colors" data-track="tab-click" data-track-tab-name="Colors">
                      {t('shingles.colors.colors')}
                    </a>
                  </li>
                  <li>
                    <a
                      className="stickynav-anchor"
                      href="#gallery"
                      data-track="tab-click"
                      data-track-tab-name="Gallery"
                    >
                      {t('shingles.gallery')}
                    </a>
                  </li>
                  <li>
                    <a
                      className="stickynav-anchor"
                      href="#features"
                      data-track="tab-click"
                      data-track-tab-name="Features"
                    >
                      {t('shingles.features.features')}
                    </a>
                  </li>
                  <li>
                    <a
                      className="stickynav-anchor"
                      href="#technical-information"
                      data-track="tab-click"
                      data-track-tab-name="Technical Information"
                    >
                      {t('shingles.technical_information')}
                    </a>
                  </li>
                  <li>
                    <a
                      className="stickynav-anchor"
                      href="#related-items"
                      data-track="tab-click"
                      data-track-tab-name="Related Items"
                    >
                      {t('shingles.related_items')}
                    </a>
                  </li>
                  <LocaleSpecific only={['en-US', 'es-US']}>
                    <li>
                      <a
                        className="stickynav-anchor"
                        href="#help-with-color"
                        data-track="tab-click"
                        data-track-tab-name="Inspiration"
                      >
                        {t('shingles.inspiration')}
                      </a>
                    </li>
                  </LocaleSpecific>
                </ul>
              </div>
            </div>
          </Conditional>

          <Conditional if={shingle.unavailable}>
            <section css={alertSpacing}>{createAlertMessage(message)}</section>
          </Conditional>

          <If condition={notFound(meta) || !selectedColor}>
            <div className="product-not-available-banner">
              <i className="fa fa-exclamation-triangle" />
              <div className="banner-text">
                {t('shingles.product_not_available')}
                <If condition={meta.zip}>{` (${meta.zip})`}</If>.{' '}
                <div className="banner-links">
                  <a onClick={openLocationModal} href="#">
                    {t('shingles.change_location')}
                  </a>{' '}
                  {t('shingles.or')}{' '}
                  <a href={PrefixWithLocale('/roofing/shingles', locale.code)}>{t('shingles.browse_products')}</a>
                </div>
              </div>
            </div>
          </If>
        </>
      );
    }
  )
);

Header.propTypes = {
  shingle: PropTypes.shape({
    proper_name: PropTypes.string.isRequired,
    hero_992x390_url: PropTypes.string.isRequired,
  }),
};

const PreviousColor = ({ style, onClick, shingle }) => (
  <a
    className="color-arrow previous-color"
    tabIndex="0"
    style={{
      display: 'block',
      textDecoration: 'none',
    }}
    onClick={onClick}
    href="#"
    data-track="view-previous"
    data-track-destination={`${shingle.proper_name} Colors - House View`}
    aria-label="Previous Shingle Color"
  >
    <i className="icon-chevron-left" />
  </a>
);

const NextColor = ({ style, onClick, shingle }) => (
  <a
    className="color-arrow next-color"
    tabIndex="0"
    style={{
      display: 'block',
      textDecoration: 'none',
    }}
    onClick={onClick}
    href="#"
    data-track="view-next"
    data-track-destination={`${shingle.proper_name} Colors - House View`}
    aria-label="Next Shingle Color"
  >
    <i className="icon-chevron-right" />
  </a>
);

const ShingleColorPreview = styled.div`
  background-image: ${props => `url(${props.background})`};
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;

  @media (max-width: 767px) {
    background-image: ${props => `url(${props.mobileBackground})`};
  }
`;

const FavoriteIcon = styled.i`
  color: ${props => (props.checked ? '#D40F7D' : '#FFFFFF')};
`;

const FavoriteIconOverlay = styled.i`
  color: ${props => (props.checked ? '#FFFFFF' : '')};
`;
const Overlap = styled.div`
  i {
    position: absolute;
  }
`;
export const Colors = connectLocation(
  connectFavorites(
    ({ shingle, selectedColor, onColorClick, onZoom, favorites, t, locale, addToFavorites, removeFromFavorites }) => {
      useCrossTabShinglesFavoritesResync();
      const isFavorite = favorites.includes(selectedColor.uuid);
      const onToggleFavorite = isFavorite ? removeFromFavorites : addToFavorites;
      shingle.shingle_colors = shingle.shingle_colors.sort((a, b) => (b.newcolor || 0) - (a.newcolor || 0));

      return (
        <section id="colors" data-testid="colors">
          <Description shingle={shingle} selectedColor={selectedColor} locale={locale} altText={altText} />

          <div tabIndex="0" className="pys__colors-carousel-container">
            <Slider prevArrow={<PreviousColor shingle={shingle} />} nextArrow={<NextColor shingle={shingle} />}>
              {[
                { url: 'house_1229x768_url2', class: 'house1' },
                { url: 'house_1229x768_url2', class: 'house2' },
              ].map(key => (
                <div key={key.url} className="pys__colors-carousel-item">
                  <div
                    className={key.class}
                    style={{
                      backgroundImage: `url(${selectedColor[key.url]})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }}
                  />
                  <div className="tablet-desktop-only">
                    <a
                      className="zoom-button"
                      data-track="zoom"
                      data-track-image-name={selectedColor.uuid}
                      href="#"
                      onClick={withDefaultPrevented(() => onZoom({ src: selectedColor[key.url] }))}
                      title={`${t('shingles.close_up_view_of')} ${selectedColor.name} ${shingle.name}`}
                    >
                      <i className="fa fa-search-plus" aria-hidden="true" />
                    </a>
                  </div>
                </div>
              ))}
            </Slider>
            <ShingleColorPreview
              background={selectedColor.swatch_768x768_url}
              mobileBackground={selectedColor.swatch_768x485_url}
              className="shingle-color-preview"
            >
              <a
                className="favorite-toggle"
                data-track={isFavorite ? 'remove-favorite' : 'make-favorite'}
                data-track-product-name={shingle.uid}
                data-track-product-color={selectedColor.name}
                onClick={withDefaultPrevented(() => onToggleFavorite(selectedColor.uuid))}
                aria-label={`${isFavorite ? t('shingles.remove_favorite') : t('shingles.add_favorite')} ${
                  selectedColor.name
                }`}
              >
                <Overlap>
                  <FavoriteIcon
                    className={isFavorite ? 'fa fa-heart' : 'fa fa-heart-o'}
                    checked={isFavorite}
                    aria-hidden="true"
                  />
                  <FavoriteIconOverlay
                    className={isFavorite ? 'fa fa-heart-o' : ''}
                    checked={isFavorite}
                    aria-hidden="true"
                  />
                </Overlap>

                <span className="sr-only">
                  <Text
                    content={`${isFavorite ? 'Remove' : 'Add'} ${selectedColor.name} ${
                      isFavorite ? 'from' : 'to'
                    } Favorites`}
                  />
                </span>
              </a>
            </ShingleColorPreview>
          </div>
          <ShingleColorDescription
            name={selectedColor.name}
            description={selectedColor.description}
            className="mobile-only"
          />
          <div data-am-region="Choose Your Shingle Color">
            <ShingleColorsListController
              recentlyViewed={false}
              shingle={shingle}
              houseView={false}
              selectedColor={selectedColor}
              onColorClick={onColorClick}
              isPDP
            />
          </div>
        </section>
      );
    }
  )
);

const WomensChoiceBanner = connectLocation(({ showWcaBanner, onClickHide, t }) => (
  <Conditional if={showWcaBanner}>
    <section>
      <div className="wca-banner">
        <span className="close" onClick={onClickHide}>
          &times;
        </span>

        <Picture
          className="logo"
          source="https://imagecdn.owenscorning.com/image/upload/f_auto,q_auto/v1765318127/WCA_2026/WCA_Image_Block_2018-2026_s2y0g7.png"
        />

        <div className="content">
          <h3>
            <Text content={t('shingles.statics.wca_banner.heading')} />
          </h3>
          <p>
            <Text content={t('shingles.statics.wca_banner.copy')} />
          </p>
        </div>
      </div>
    </section>
  </Conditional>
));

const Actions = connectLocation(
  connectFavorites(({ shingle, zip, t, favorites, locale }) => {
    const favoritesCount = favorites.length;
    const linkProps = {
      favorites: {
        href: PrefixWithLocale('/roofing/shingles/favorites', locale.code),
        analytics: {
          track: 'learn-more',
          'element-location': 'main section CTA',
          destination: 'Favorites',
        },
        children: <Text content={`${t('shingles.favorites')} (${favoritesCount})`} />,
      },
      orderSamples: {
        href: orderSamplesUrl(locale.code),
        analytics: {
          track: 'order-sample',
          'product-name': 'Shingle Sample',
        },
        children: <Text content={t('shingles.color_samples.primary_cta_text')} />,
      },
    };
    let secondaryLink;
    let tertiaryLink = null;
    // If we have "order samples" link, "favorites" link gets demoted to tertiary, text link
    if (hasOrderSamples(locale.code, shingle.uid)) {
      secondaryLink = linkProps.orderSamples;
      tertiaryLink = linkProps.favorites;
    } else {
      secondaryLink = linkProps.favorites;
    }

    return (
      <div className="pdp-actions">
        <div className="bg-full-bleed oc-stickynav non-sticky">
          <div className="stickynav-container">
            <div className="cta-group cta-group-wrap">
              <FeatureFlagged except={['contractor_referral']}>
                <LocaleSpecific only={['en-US', 'es-US']}>
                  <a
                    href={PrefixWithLocale('/roofing/contractors', locale.code)}
                    className="oc-cta-button"
                    data-track="find-a-professional"
                    data-track-professional="contractor"
                  >
                    {t('shingles.find_contractor')}
                  </a>
                </LocaleSpecific>
                <LocaleSpecific only={['en-CA', 'fr-CA']}>
                  <a
                    href={`http://roofingca.owenscorning.com/homeowner/locator/?zip=${
                      zip ? encodeURIComponent(zip) : ''
                    }`}
                    className="oc-cta-button"
                    data-track="find-a-professional"
                    data-track-professional="contractor"
                  >
                    {t('shingles.find_contractor')}
                  </a>
                </LocaleSpecific>
              </FeatureFlagged>

              <Cta color="white" className="oc-cta-button" {...secondaryLink} />
            </div>

            <ul className="stickynav-links">
              {tertiaryLink && (
                <li>
                  <Link {...tertiaryLink} />
                </li>
              )}

              <LocaleSpecific only={['en-US', 'es-US']}>
                <li>
                  <a
                    href={PrefixWithLocale('/roofing/roof-visualizer', locale.code)}
                    data-track="learn-more"
                    data-track-element-location="main section CTA"
                    data-track-destination="DEQ"
                  >
                    {t('shingles.try_on_this_roof')}
                  </a>
                </li>
              </LocaleSpecific>

              <LocaleSpecific only={['en-US', 'es-US']}>
                <Conditional if={shingle.shingle_series && shingle.shingle_series.uid == 'duration'}>
                  <li>
                    <a
                      href={PrefixWithLocale('/roofing/design-inspire/colors-of-the-season', 'en-US')}
                      data-track="learn-more"
                      data-track-element-location="main section CTA"
                      data-track-destination="Design & Inspire"
                    >
                      {t('shingles.explore_style_boards')}
                    </a>
                  </li>
                </Conditional>
              </LocaleSpecific>

              <LocaleSpecific only={['en-US', 'es-US']}>
                <li>
                  <a
                    href={PrefixWithLocale(`/roofing/where-to-buy?zip=${zip ? encodeURIComponent(zip) : ''}`, 'en-US')}
                    data-track="where-to-buy"
                    data-track-buy-type="Retailer/Distributor"
                  >
                    {t('shingles.where_to_buy')}
                  </a>
                </li>
              </LocaleSpecific>

              <LocaleSpecific only={['en-CA']}>
                <li>
                  <a
                    href={`https://roofingca.owenscorning.com/professional/where-to-buy/?zip=${
                      zip ? encodeURIComponent(zip) : ''
                    }#retailers`}
                    data-track="where-to-buy"
                    data-track-buy-type="Retailer/Distributor"
                  >
                    {t('shingles.where_to_buy')}
                  </a>
                </li>
              </LocaleSpecific>

              <LocaleSpecific only={['fr-CA']}>
                <li>
                  <a
                    href={`https://roofingca.owenscorning.com/professional/where-to-buy/?zip=${
                      zip ? encodeURIComponent(zip) : ''
                    }#retailers`}
                    data-track="where-to-buy"
                    data-track-buy-type="Retailer/Distributor"
                  >
                    {t('shingles.where_to_buy')}
                  </a>
                </li>
              </LocaleSpecific>
            </ul>
          </div>
        </div>
      </div>
    );
  })
);

const Description = connectLocation(({ shingle, selectedColor, locale, altText, t }) => (
  <section>
    <div className="content">
      <h2>
        <Text content={shingle.headline} />
      </h2>
      <p>
        <RichText content={shingle.description} />
      </p>
    </div>
  </section>
));

const ContractorList = connectLocation(({ contractors, onRequestAQuoteClick, t, locale }) => (
  <>
    {contractors.map(contractor => (
      <ContractorCard {...{ contractor, onRequestAQuoteClick, t, locale }} />
    ))}
  </>
));

const FindAContractorView = connectLocation(
  ({ contractors, onRequestAQuoteClick, t, locale, address: { city, region } }) => (
    <section id="fac" className="pys__find-a-contractor">
      <div className="condensed-width-content">
        <h2>{t('shingles.find_contractor')}</h2>
        <p className="subtitle">
          {t('shingles.get_roof_installed')} {city}, {region}
        </p>

        {isEmpty(contractors.contractors) ? (
          <p>
            <Text content={t('shingles.no_contractors_found')} />
          </p>
        ) : (
          <div className="contractor-cards">
            <ContractorList
              contractors={contractors.contractors.slice(0, 6)}
              onRequestAQuoteClick={onRequestAQuoteClick}
            />
          </div>
        )}

        <a
          href={PrefixWithLocale('/roofing/contractors', locale.code)}
          className="oc-cta-button transparent"
          data-track="find-a-professional"
          data-track-professional="contractor"
        >
          <Text content={t('shingles.view_more_contractors')} />
        </a>
      </div>
    </section>
  )
);

class FindAContractor extends Component {
  constructor(props) {
    super(props);

    this.moreRef = createRef();

    this.state = {
      moreHeight: 0,
    };

    this.updateMoreHeight = this.updateMoreHeight.bind(this);
  }

  toggleShowMore() {
    this.setState(({ moreHeight }) => ({ moreHeight: moreHeight ? 0 : this.maxMoreHeight }));
  }

  updateMoreHeight() {
    this.setState(({ moreHeight }) => ({ moreHeight: moreHeight ? this.maxMoreHeight : 0 }));
  }

  get maxMoreHeight() {
    return this.moreRef.current.scrollHeight;
  }

  componentDidMount() {
    window.addEventListener('resize', this.updateMoreHeight);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateMoreHeight);
  }

  render() {
    return (
      <FindAContractorView
        onToggleMoreClick={() => this.toggleShowMore()}
        moreHeight={this.state.moreHeight}
        moreRef={this.moreRef}
        {...this.props}
      />
    );
  }
}

export const GalleryImagesList = connectLocation(({ t, images, onImageClick, customLocale }) => {
  if (!images || !images.length) {
    return null;
  }

  const toImageItem = (img, idx) => {
    const imageName = getMediaTitle({ t, img });
    return (
      <div className="resource" key={idx}>
        <a
          href="#"
          className={img.youtube_id ? 'video' : 'image'}
          onClick={withDefaultPrevented(() => onImageClick(img))}
          data-track="zoom"
          data-track-image-name={img.name}
          title={img.name}
        >
          <Picture
            source={img.img_url}
            alt={altText(img, 'img', customLocale, img.name)}
            variants={[
              {
                aspectRatio: '16:9',
                crop: 'fill',
                width: 486,
                media: '(min-width: 1200px) and (min-resolution: 2dppx)',
                quality: 30,
              },
              {
                aspectRatio: '16:9',
                crop: 'fill',
                width: 243,
                media: '(min-width: 1200px)',
                quality: 60,
              },
              {
                aspectRatio: '16:9',
                crop: 'fill',
                width: 452,
                media: '(min-width: 768px)',
                quality: 30,
              },
              {
                aspectRatio: '16:9',
                crop: 'fill',
                width: 856,
                media: '(min-width: 428px)',
                quality: 20,
              },
              {
                aspectRatio: '16:9',
                crop: 'fill',
                width: 690,
                media: '(min-width: 375px)',
                quality: 20,
              },
              {
                aspectRatio: '16:9',
                crop: 'fill',
                width: 580,
                quality: 20,
              },
            ]}
          />
        </a>
        <p>{imageName}</p>
      </div>
    );
  };

  return <div className="oc-gallery gallery">{images.map(toImageItem)}</div>;
});

const Gallery = connectLocation(({ shingle, onImageClick, custom_locale, t }) => (
  <div id="gallery" className="gallery-section">
    <h2>{t('shingles.gallery')}</h2>

    <LocaleSpecific only={['en-US', 'es-US']}>
      <GalleryImagesList
        images={[...shingle.gallery_images, ...shingle.gallery_videos]}
        onImageClick={onImageClick}
        customLocale={custom_locale}
      />
    </LocaleSpecific>

    <LocaleSpecific except={['en-US', 'es-US']}>
      <GalleryImagesList images={shingle.gallery_images} onImageClick={onImageClick} customLocale={custom_locale} />
    </LocaleSpecific>
  </div>
));

const RelatedItem = connectLocation(({ t, locale, itemKey }) => {
  const translateScoped = key => t(`shingles.related.${itemKey}.${key}`);
  return (
    <div className="related-item">
      <Picture className="related-item__image" source={translateScoped('image_url')} alt={translateScoped('alt')} />
      <h3 className="body-font">
        <Text content={translateScoped('title')} />
      </h3>
      <p>
        <Text content={translateScoped('description')} />
      </p>
      <a
        href={PrefixWithLocale(translateScoped('href'), locale.code)}
        className="oc-cta-button transparent"
        data-track="learn-more"
        data-track-element-location="main section CTA"
        data-track-destination={dataTrackAttribute(translateScoped('href'))}
      >
        <Text content={t('shingles.featuresAndBenefits.cta')} />
      </a>
    </div>
  );
});

const Related = connectLocation(({ t }) => (
  <div id="related-items" className="gallery gallery-section">
    <h2 className="related-header">{t('shingles.related.title')}</h2>
    <RelatedItem itemKey="finishing_touch" />
    <RelatedItem itemKey="shingle_deep" />
  </div>
));

export const TechnicalCharacteristics = connectLocation(({ shingle, t }) => (
  <div className="oc-table-responsive">
    <table className="oc-table table-striped">
      <caption className="sr-only">
        <Text content={`${shingle.name} ${t('shingles.name')} ${t('shingles.technical_characteristics')}`} />
      </caption>
      <thead>
        <tr>
          <th>
            <Text content={t('shingles.property_unit')} />
          </th>
          <th>
            <Text content={t('shingles.value')} />
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            <Text content={t('shingles.warranty')} />
          </td>
          <td>
            <Text content={shingle.warranty} />
          </td>
        </tr>
        <tr>
          <td>
            <Text content={t('shingles.wind_resistance')} />
          </td>
          <td>
            <Text content={shingle.wind_resistance} />
          </td>
        </tr>
        <tr>
          <td>
            <Text content={t('shingles.algae_resistance')} />
            <sup style={{ fontSize: '.8em' }}>§</sup>
          </td>
          <td>
            <Text content={shingle.algae_resistance || 'N/A'} />
            <sup style={{ fontSize: '.8em' }}>*</sup>
          </td>
        </tr>
        <tr>
          <td>
            <Text content={t('shingles.nominal_size')} />
          </td>
          <td>
            <Text content={shingle.nominal_size} />
          </td>
        </tr>
        <tr>
          <td>
            <Text content={t('shingles.exposure')} />
          </td>
          <td>
            <Text content={shingle.exposure} />
          </td>
        </tr>
        <tr>
          <td>
            <Text content={t('shingles.shingles_per_sq')} />
          </td>
          <td>
            <Text content={shingle.shingles_per_sq} />
          </td>
        </tr>
        <tr>
          <td>
            <Text content={t('shingles.bundles_per_sq')} />
          </td>
          <td>
            <Text content={shingle.bundles_per_sq} />
          </td>
        </tr>
        <tr>
          <td>
            <Text content={t('shingles.coverage_per_sq')} />
          </td>
          <td>
            <Text content={shingle.coverage_per_sq} />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
));

const StandardsList = ({ shingle_standards = [] }) => (
  <ul style={{ paddingInlineStart: '20px' }}>
    {shingle_standards.map((s, idx) => (
      <li
        key={idx}
        style={{
          paddingLeft: '15px',
          fontSize: 14,
          lineHeight: '16px',
        }}
      >
        <Text content={s.name} />
      </li>
    ))}
  </ul>
);

const SizeType = ({ size_bytes, mime_type }) => {
  let size = Math.round((size_bytes / (1024 * 1024)) * 10) / 10;
  size = size ? `${size} MB` : '';
  const customType = mime_type === 'application/pdf' ? 'PDF | ' : '';

  return (
    <span>
      <Text content={`${customType} ${size}`} />
    </span>
  );
};

const FileIcon = ({ mime_type, url }) => {
  let classname;
  url = url || '';
  if (mime_type) {
    classname = mime_type === 'application/pdf' ? 'fa-file-pdf-o' : 'fa-file-o';
  } else {
    classname = url.includes('application/pdf') ? 'fa-file-pdf-o' : 'fa-file-o';
  }

  return <i className={`fa ${classname}`} style={{ paddingRight: '4px' }} />;
};

const DocumentsList = ({ shingle_documents = [] }) => (
  <ul style={{ paddingLeft: 0 }}>
    {shingle_documents.map((d, idx) => (
      <li key={idx} style={{ listStyleType: 'none' }}>
        <a
          href={d.url}
          style={{
            textDecoration: 'none',
            fontSize: 14,
            lineHeight: '16px',
          }}
        >
          <FileIcon {...d} />{' '}
          <strong>
            <Text content={d.name} />
          </strong>{' '}
          <SizeType {...d} />
        </a>
      </li>
    ))}
  </ul>
);

const Technical = connectLocation(({ shingle, t }) => (
  <section id="technical-information">
    <h2 style={{ paddingBottom: '30px' }}>{t('shingles.technical_information')}</h2>
    <div className="pys__technical-information-container">
      <div className="pys__technical-characteristics">
        <h3 className="body-font">
          <Text content={t('shingles.technical_characteristics')} />
          &nbsp;
          <span style={{ fontSize: '.8em' }}>
            <Text content={t('shingles.nominal_values')} />
          </span>
        </h3>
        <TechnicalCharacteristics shingle={shingle} />
      </div>
      <div className="pys__applicable-standards">
        <h3 className="h4 body-font">
          <Text content={t('shingles.applicable_standards')} />
        </h3>
        <StandardsList {...shingle} />
      </div>
      <div className="pys__technical-documents">
        <h3 className="h4 body-font">
          <Text content={t('shingles.technical_documents')} />
        </h3>
        <DocumentsList {...shingle} />
      </div>
    </div>
  </section>
));

Technical.propTypes = {
  shingle: PropTypes.shape({
    warranty: PropTypes.string.isRequired,
    wind_resistance: PropTypes.string.isRequired,
    algae_resistance: PropTypes.string,
    nominal_size: PropTypes.string.isRequired,
    exposure: PropTypes.string.isRequired,
    shingles_per_sq: PropTypes.string.isRequired,
    bundles_per_sq: PropTypes.string.isRequired,
    coverage_per_sq: PropTypes.string.isRequired,
  }),
};
// Static content

const GalleryModalMedia = ({ image, locale }) => {
  if (!image) return null;
  if (image.youtube_id) return <Video src={image.youtube_id} />;
  return (
    <Picture
      source={image.img_url}
      alt={altText(image, 'img', locale, image.name)}
      transform={{
        crop: 'fill',
      }}
    />
  );
};

export const GalleryModal = connectLocation(({ t, image, onClose, customLocale }) => {
  const className = `no-borders zoom-modal gallery-preview-modal ${
    image && image.youtube_id ? 'video-modal' : 'image-modal'
  }`;
  const mediaTitle = getMediaTitle({ t, img: image });

  return (
    <Modal name="Gallery Image Preview" open={!!image} onClose={onClose} className={className}>
      <GalleryModalMedia image={image} locale={customLocale} />
      <h3 className="image-name">
        <Text content={mediaTitle || ''} />
      </h3>
    </Modal>
  );
});

const ImageZoomModal = ({ onClose, ...props }) => (
  <Modal open={props.src} onClose={onClose} className="no-borders zoom-modal image-modal" size="lg">
    <img style={{ display: 'block', width: '100%' }} {...props} />
  </Modal>
);

const FlexSeasons = connectLocation(({ t }) => (
  <section>
    <ContentAndImage
      ratio="1-1"
      contentOrder="last"
      noMargin
      alt={t('shingles.statics.flex_seasons.alt')}
      video="Zi3tsztUl_8"
    >
      <H2>
        <Text content={t('shingles.statics.flex_seasons.heading')} />
      </H2>
      <p>
        <Text content={t('shingles.statics.flex_seasons.text')} />
      </p>
    </ContentAndImage>
  </section>
));

const BreadcrumbItems = connectLocation(({ t, locale }) => (
  <Breadcrumbs
    items={[
      { text: t('shingles.breadcrumbs.roofing'), href: PrefixWithLocale('/roofing', locale) },
      { text: t('shingles.breadcrumbs.shingles'), href: PrefixWithLocale('/roofing/shingles', locale) },
    ]}
  />
));

const WoodmoorOrWoodcrestAttentionBar = connectLocation(({ locale, shingle }) => (
  <If condition={shingle.uid === 'woodmoor' || shingle.uid === 'woodcrest'}>
    <section>
      <Reusable name="Woodmoor and Woodcrest Attention Bar" path="roofing/shingles" language={locale?.code} />
    </section>
  </If>
));

WoodmoorOrWoodcrestAttentionBar.propTypes = {
  locale: PropTypes.string.isRequired,
  shingle: PropTypes.shape({
    uid: PropTypes.string.isRequired,
  }).isRequired,
};

class ShinglesPDP extends Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedColor: this.props.selectedColor,
      favoritesStatus: null,
      lastFavorite: null,
      showWcaBanner: Cookies.get('closed_wca_banner') === undefined,
    };
    this.closeWcaBanner = this.closeWcaBanner.bind(this);
  }

  onColorClick(selectedColor) {
    const path = `${window.location.pathname}?color=${selectedColor.uid}`;

    history.replaceState('shingle', document.title, path);
    this.props.addToRecentlyViewed(selectedColor.uuid);

    this.setState({ selectedColor });
  }

  closeWcaBanner() {
    Cookies.set('closed_wca_banner', 'true');
    this.setState({ showWcaBanner: false });
  }

  componentDidMount() {
    if (this.props.selectedColor && this.props.selectedColor.uuid) {
      this.props.addToRecentlyViewed(this.props.selectedColor.uuid);
    }
  }

  render() {
    const { shingle, favorites, locale } = this.props;
    const { selectedColor, previewGalleryImage, requestAQuoteContractor, zoomImage } = this.state;

    return (
      <Provider store={store}>
        <BreadcrumbsContainer>
          <BreadcrumbItems locale={locale} />
          <LocationSwitcher showLocale postalRequired />
        </BreadcrumbsContainer>
        <Header shingle={shingle} meta={this.props.meta} selectedColor={selectedColor} />

        <LocaleSpecific only={['en-US', 'es-US']}>
          <WomensChoiceBanner showWcaBanner={this.state.showWcaBanner} onClickHide={this.closeWcaBanner} />
        </LocaleSpecific>

        {selectedColor && (
          <Colors
            shingle={shingle}
            selectedColor={selectedColor}
            onColorClick={color => this.onColorClick(color)}
            favorites={favorites}
            onZoom={zoomImage => this.setState({ zoomImage })}
          />
        )}

        <Actions shingle={shingle} />

        <If condition={shingle.uid === 'trudefinition-duration'}>
          <Reusable language={locale} name="LASSY Colors Coming Soon" path="roofing" />
        </If>

        <Reusable language={locale} name="Color Disclaimer" path="roofing" />

        <If condition={shingle.uid === 'trudefinition-duration-flex'}>
          <LocaleSpecific only={['en-US', 'es-US']}>
            <FlexSeasons />
          </LocaleSpecific>
        </If>

        {/** NOTE: `locale` is actually locale.code already. */}
        <If condition={hasOrderSamples(locale, shingle.uid)}>
          <Section fluid>
            <ColorSamplesContentAndImage />
          </Section>
        </If>

        <FeatureFlagged except={['contractor_referral']}>
          <FindAContractor
            contractors={this.props.contractors}
            onRequestAQuoteClick={requestAQuoteContractor => {
              this.setState({ requestAQuoteContractor });
            }}
          />
        </FeatureFlagged>
        <Gallery
          shingle={shingle}
          onImageClick={previewGalleryImage => this.setState({ previewGalleryImage })}
          custom_locale={locale}
        />
        <Features shingle={shingle} />
        <div className="pdp__divider" />
        <Technical shingle={shingle} />
        <Related />
        <WoodmoorOrWoodcrestAttentionBar shingle={shingle} />
        <LocaleSpecific only={['en-US', 'es-US']}>
          <HelpWithColor />
        </LocaleSpecific>
        <LocaleSpecific only={['en-US', 'es-US']}>
          <WomensChoiceAward />
        </LocaleSpecific>
        <PYSNotes shingle={shingle} />
        <GalleryModal
          image={previewGalleryImage}
          onClose={() => this.setState({ previewGalleryImage: null })}
          customLocale={locale}
        />
        <ImageZoomModal onClose={() => this.setState({ zoomImage: null })} {...(zoomImage || {})} />
        <RequestAQuoteModal
          open={!!requestAQuoteContractor}
          contractor={requestAQuoteContractor}
          onClose={() => this.setState({ requestAQuoteContractor: null })}
          source="pdp"
          raqFormName={document.getElementById('raq-modal').dataset.formName}
        />
      </Provider>
    );
  }
}

const ConnectedShinglesPDP = connectFavorites(connectRecentlyViewed(ShinglesPDP));

class ShinglesPDPContainer extends Component {
  render() {
    return (
      <Provider store={store}>
        <ConnectedShinglesPDP {...this.props} />
      </Provider>
    );
  }
}

export default withFeatureFlagsProvider(ShinglesPDPContainer);
